// DATA-12381: derives a curated historical record's tree-sourced fields from the person summary the
// console already serves (GET /service/dmc/persons/{pid}/summary?forward=true).
//
// This is the client-side twin of the worker's TfPersonToHistoricalPersonMapper. The worker reads a
// TfPerson; this reads the console's summary DTO. Different shapes, deliberately the same rules, because
// a record pre-populated here and one derived by the nightly run have to agree -- BR-10 makes the tree
// authoritative, so a value that differs between the two routes is a record that flips every night.
//
// Pure: no I/O. Everything it cannot derive comes back undefined rather than '', because the two mean the
// same thing downstream (BR-5) and undefined is the honest one.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// Days in each month, ignoring the year: Feb 29 is allowed everywhere, because the tree's calendar for
// old dates is not the proleptic ISO one. What is rejected is a day no year of that month has.
const MAX_DAY = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
/** A GEDCOM-X formal date: a signed four-digit year, then optional month and day. */
const FORMAL_DATE = /^([+-])(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/
const GEDCOMX = 'http://gedcomx.org/'

/**
 * Renders a GEDCOM-X formal date the way the worker's DatabaseService.asString renders a standardized
 * one: `21 Nov 1922`, `Nov 1922` or `1922` as the date's granularity allows. A range takes its first
 * component. Answers undefined for anything unparseable, and for a year under 100, which the save path
 * rejects outright -- producing one here would only be storing something it would refuse.
 *
 * Kept byte-identical to TfPersonToHistoricalPersonMapper.displayFormOf. If that one changes, this must.
 */
export function displayFormOf(formal) {
  if (!formal?.trim()) {
    return undefined
  }
  const match = FORMAL_DATE.exec(formal.trim().split('/')[0].split('T')[0])
  if (!match) {
    return undefined
  }
  const [, sign, yearText, monthText, dayText] = match
  const year = sign === '-' ? -Number.parseInt(yearText, 10) : Number.parseInt(yearText, 10)
  // Sign first: asString rejects 0..99 only, and renders BCE years of any size.
  if (year >= 0 && year < 100) {
    return undefined
  }
  if (!monthText) {
    return `${year}`
  }
  const monthIndex = Number.parseInt(monthText, 10) - 1
  if (monthIndex < 0 || monthIndex > 11) {
    return undefined
  }
  if (!dayText) {
    return `${MONTHS[monthIndex]} ${year}`
  }
  const day = Number.parseInt(dayText, 10)
  if (day < 1 || day > MAX_DAY[monthIndex]) {
    return undefined
  }
  return `${day} ${MONTHS[monthIndex]} ${year}`
}

function blankToUndefined(value) {
  return typeof value === 'string' && value.trim() ? value : undefined
}

/**
 * The first normalized rendering the tree offers, whatever its language. Not filtered to en-US: the list
 * is ordered by the tree's own preference, and a record with only one rendering should use it rather than
 * fall back to raw text because the language tag did not match.
 */
function firstNormalized(normalized) {
  if (!Array.isArray(normalized)) {
    return undefined
  }
  return normalized.map((entry) => blankToUndefined(entry?.value)).find(Boolean)
}

function factOf(summary, shortType) {
  const facts = Array.isArray(summary?.factConclusions) ? summary.factConclusions : []
  return facts.find((fact) => fact?.value?.type === `${GEDCOMX}${shortType}`)
}

/** Formal first, then normalized, then the raw original, matching the worker's dateOf. */
function dateOf(fact) {
  const date = fact?.value?.date
  if (!date) {
    return undefined
  }
  return displayFormOf(date.formal) ?? firstNormalized(date.normalized) ?? blankToUndefined(date.original)
}

/** `original`, not the standardized names list, matching the worker's placeOf. */
function placeOf(fact) {
  return blankToUndefined(fact?.value?.place?.original)
}

function partOf(nameForms, shortType) {
  const parts = Array.isArray(nameForms) ? nameForms[0]?.parts : undefined
  if (!Array.isArray(parts)) {
    return undefined
  }
  return blankToUndefined(parts.find((part) => part?.type === `${GEDCOMX}${shortType}`)?.value)
}

function nameOf(nameForms) {
  return {
    givenName: partOf(nameForms, 'Given'),
    surname: partOf(nameForms, 'Surname'),
  }
}

/** "Male" or "Female" only. Unknown, absent or unrecognized yields undefined, as the worker's genderOf. */
function genderOf(summary) {
  const gender = typeof summary?.gender === 'string' ? summary.gender.trim().toLowerCase() : ''
  if (gender === 'female') {
    return 'Female'
  }
  return gender === 'male' ? 'Male' : undefined
}

/**
 * The preferred parent pairing's two parents, in declaration order. BR-14: nothing when the tree marks no
 * pairing preferred -- better absent than an arbitrary pick. parent1/parent2 is a declaration order, not
 * a father and a mother; the console form never asks which is which.
 */
function parentsOf(summary) {
  const pairing = (summary?.relationships?.parents ?? []).find((entry) => entry?.preferred)
  const parent1 = nameOf(pairing?.parent1?.nameForms)
  const parent2 = nameOf(pairing?.parent2?.nameForms)
  return {
    parent1GivenName: parent1.givenName,
    parent1Surname: parent1.surname,
    parent2GivenName: parent2.givenName,
    parent2Surname: parent2.surname,
  }
}

/**
 * BR-14: the spouse only when there is exactly one, rather than choosing between spouses the curated
 * record has no room for. Joseph Smith Jr has around thirty, and derives none.
 */
function spouseOf(summary) {
  const spouses = summary?.relationships?.spouses
  if (!Array.isArray(spouses) || spouses.length !== 1) {
    return { spouseGivenName: undefined, spouseSurname: undefined }
  }
  const spouse = nameOf(spouses[0]?.spouse?.nameForms)
  return { spouseGivenName: spouse.givenName, spouseSurname: spouse.surname }
}

/**
 * @param summary the console's person summary DTO
 * @returns `{ pid, fields }` -- the same contract the worker's pre-populate endpoint returns, so the
 *          dialog does not care which route filled it in. `pid` is the summary's own id, which is already
 *          the survivor when the request was made with forward=true (BR-16).
 */
export function mapSummaryToHistoricalFields(summary) {
  const birth = factOf(summary, 'Birth')
  const death = factOf(summary, 'Death')
  const { givenName, surname } = nameOf(summary?.nameConclusion?.value?.nameForms)
  return {
    pid: blankToUndefined(summary?.id),
    fields: {
      givenName,
      surname,
      birthDate: dateOf(birth),
      birthPlace: placeOf(birth),
      gender: genderOf(summary),
      deathDate: dateOf(death),
      deathPlace: placeOf(death),
      ...parentsOf(summary),
      ...spouseOf(summary),
    },
  }
}
